import { nanoid } from 'nanoid'
import * as THREE from 'three'
import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import {
  applyMeshCoordinateOptions,
  validateMeshCoordinateOptions,
} from '@/plugins/mesh/applyMeshCoordinateOptions'
import {
  loadMeshClipWebGpuMaterialModule,
  MeshClipUniformBinding,
  registerMeshClipWebGpuMaterialModule,
} from '@/plugins/mesh/clipping/MeshClipShader'
import { MeshClipStateSet } from '@/plugins/mesh/clipping/MeshClipStateSet'
import { MeshFormatNotSupportedError, MeshLoadCancelledError } from '@/plugins/mesh/errors'
import { estimateLoadedMeshGpuBytes } from '@/plugins/mesh/loaders/estimateLoadedMeshGpuBytes'
import { GlbMeshLoader, GltfMeshLoader } from '@/plugins/mesh/loaders/GltfMeshLoader'
import { describeMeshSource } from '@/plugins/mesh/loaders/loaderUtils'
import { MeshKtx2Service } from '@/plugins/mesh/loaders/MeshKtx2Service'
import { MeshResultJsonLoader } from '@/plugins/mesh/loaders/MeshResultJsonLoader'
import type { MeshLoadContext } from '@/plugins/mesh/loaders/meshLoadContext'
import { PlyMeshLoader } from '@/plugins/mesh/loaders/PlyMeshLoader'
import { MeshAppearanceController } from '@/plugins/mesh/MeshAppearanceController'
import { MeshLoadingPreview } from '@/plugins/mesh/MeshLoadingPreview'
import { MeshPartBatcher } from '@/plugins/mesh/MeshPartBatcher'
import { MeshPickIndex } from '@/plugins/mesh/MeshPickIndex'
import { classifyMeshMaterial } from '@/plugins/mesh/MeshPrimitiveNormalizer'
import { OptimizingMeshLoader } from '@/plugins/mesh/optimization/OptimizingMeshLoader'
import type {
  MeshAppearance,
  MeshHandle,
  MeshLoadRequest,
  MeshLoadResult,
  MeshPluginOptions,
  MeshRenderClass,
} from '@/plugins/mesh/types'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import type { EngineContext } from '@/runtime/EngineContext'
import { LoaderRegistry } from '@/runtime/LoaderRegistry'
import { evaluateCompiledClipState } from '@/shared/clipping/CompiledClipState'
import type { IDracoDecodeService } from '@/shared/draco/DracoDecodeService'
import { createDracoDecodeService } from '@/shared/draco/DracoDecodeService'
import { getLoggerManager, type ILogger } from '@/shared/logging'
import type { AssetLoader, PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { AssetId, ClipVolume, Disposable, PickQuery, PickResult } from '@/shared/types/core'
import type { AssetPlugin } from '@/shared/types/plugin'
import type { ViewFrameContext } from '@/shared/types/view'
import { getPickRayDistance, isPickPointAtOrBeyondMinDistance } from '@/shared/utils/pickDepth'

interface MeshAssetRecord {
  readonly handle: MeshHandle
  readonly result: MeshLoadResult
  readonly appearance: MeshAppearanceController
  readonly pickIndex: MeshPickIndex
  mounted: boolean
}

interface RendererState {
  readonly target: THREE.WebGLRenderTarget | null
  readonly viewport: THREE.Vector4 | null
  readonly scissor: THREE.Vector4 | null
  readonly scissorTest: boolean
  readonly autoClear: boolean
}

function normalizeFormat(format: string): string {
  return format.trim().toLowerCase()
}

function withNormalizedFormat(
  loader: AssetLoader<MeshLoadRequest, MeshLoadResult>
): AssetLoader<MeshLoadRequest, MeshLoadResult> {
  const format = normalizeFormat(loader.format)
  if (format === loader.format) return loader
  return {
    format,
    detectPriority: loader.detectPriority,
    canLoad: loader.canLoad ? (request) => loader.canLoad?.(request) ?? false : undefined,
    load: (request, context) => loader.load(request, context),
  }
}

function createLoaderRegistry(
  options: MeshPluginOptions,
  draco?: IDracoDecodeService
): LoaderRegistry<MeshLoadRequest, MeshLoadResult> {
  const byFormat = new Map<string, AssetLoader<MeshLoadRequest, MeshLoadResult>>()
  const builtins: AssetLoader<MeshLoadRequest, MeshLoadResult>[] = [
    new PlyMeshLoader({
      defaultMaterial: options.defaultMaterial,
      generateNormals: options.generateNormals,
    }),
    new GltfMeshLoader({
      draco,
      loading: options.gltf,
      optimization: options.optimization,
    }),
    new GlbMeshLoader({
      draco,
      loading: options.gltf,
      optimization: options.optimization,
    }),
    new MeshResultJsonLoader({
      draco,
      loading: options.gltf,
      optimization: options.optimization,
      maxConcurrentLoads: options.meshResult?.maxConcurrentLoads,
    }),
  ]
  for (const loader of builtins) byFormat.set(normalizeFormat(loader.format), loader)
  for (const loader of options.extraLoaders ?? []) {
    byFormat.set(normalizeFormat(loader.format), loader)
  }

  const registry = new LoaderRegistry<MeshLoadRequest, MeshLoadResult>()
  for (const loader of byFormat.values()) {
    const normalizedLoader = withNormalizedFormat(loader)
    registry.register(
      options.optimizer
        ? new OptimizingMeshLoader(normalizedLoader, options.optimizer, options.optimization)
        : normalizedLoader
    )
  }
  return registry
}

function saveRendererState(renderer: THREE.WebGLRenderer): RendererState {
  return {
    target: renderer.getRenderTarget(),
    viewport:
      typeof renderer.getViewport === 'function' ? renderer.getViewport(new THREE.Vector4()) : null,
    scissor:
      typeof renderer.getScissor === 'function' ? renderer.getScissor(new THREE.Vector4()) : null,
    scissorTest: typeof renderer.getScissorTest === 'function' ? renderer.getScissorTest() : false,
    autoClear: renderer.autoClear,
  }
}

function forceDoubleSidedMaterials(result: MeshLoadResult): void {
  const materials = new Set<THREE.Material>()
  for (const primitive of result.primitives) {
    materials.add(primitive.material)
    const objectMaterials = Array.isArray(primitive.object.material)
      ? primitive.object.material
      : [primitive.object.material]
    for (const material of objectMaterials) materials.add(material)
  }
  for (const material of materials) {
    if (material.side === THREE.DoubleSide) continue
    material.side = THREE.DoubleSide
    material.needsUpdate = true
  }
}

function restoreRendererState(renderer: THREE.WebGLRenderer, state: RendererState): void {
  renderer.setRenderTarget(state.target)
  if (state.viewport) {
    renderer.setViewport(state.viewport.x, state.viewport.y, state.viewport.z, state.viewport.w)
  }
  if (state.scissor) {
    renderer.setScissor(state.scissor.x, state.scissor.y, state.scissor.z, state.scissor.w)
  }
  renderer.setScissorTest(state.scissorTest)
  renderer.autoClear = state.autoClear
}

function restoreChildIndex(parent: THREE.Object3D, child: THREE.Object3D, index: number): void {
  parent.add(child)
  const currentIndex = parent.children.indexOf(child)
  if (index < 0 || index >= parent.children.length || currentIndex === index) return
  parent.children.splice(currentIndex, 1)
  parent.children.splice(index, 0, child)
}

function addDefaultMeshLights(scene: THREE.Scene): void {
  const hemisphere = new THREE.HemisphereLight(0xdbeafe, 0x1f2937, 1.1)
  hemisphere.name = 'mesh:light:hemisphere'

  const key = new THREE.DirectionalLight(0xffffff, 2.4)
  key.name = 'mesh:light:key'
  key.position.set(4, -6, 8)

  const fill = new THREE.DirectionalLight(0x93c5fd, 0.8)
  fill.name = 'mesh:light:fill'
  fill.position.set(-5, 3, 2)

  scene.add(hemisphere, key, fill)
}

/** 静态 PLY、glTF 和 GLB Mesh 资产插件。 */
export class MeshPlugin implements AssetPlugin<MeshLoadRequest, MeshHandle> {
  readonly name = 'mesh'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  readonly kind = 'mesh' as const
  readonly priority = 40
  readonly phases = [RenderPhase.MainOpaque, RenderPhase.MainTransparent] as const
  readonly dependencies = ['RenderPipeline', 'PickingService'] as const

  private readonly loaderRegistry: LoaderRegistry<MeshLoadRequest, MeshLoadResult>
  private readonly _ktx2Service?: MeshKtx2Service
  private readonly _dracoService?: IDracoDecodeService
  private readonly _ownsDracoService: boolean
  private readonly autoMount: boolean
  private readonly opaqueScene = new THREE.Scene()
  private readonly transparentScene = new THREE.Scene()
  private readonly records = new Map<AssetId, MeshAssetRecord>()
  private readonly pendingLoads = new Map<AssetId, AbortController>()
  private readonly clipStates = new MeshClipStateSet()
  private readonly clipBinding = new MeshClipUniformBinding()
  private readonly warnedClipMaterialTypes = new Set<string>()

  private context!: EngineContext
  private meshRoot: THREE.Group | null = null
  private log: ILogger | null = null
  private renderLog: ILogger | null = null
  private clipLog: ILogger | null = null
  private clipSubscription: Disposable | null = null
  private clipBackend: GraphicsBackendKind = 'webgl2'
  private started = false
  private disposed = false

  constructor(options: MeshPluginOptions = {}) {
    this._ktx2Service = options.ktx2 ? new MeshKtx2Service(options.ktx2) : undefined
    this._ownsDracoService = !!options.draco && !('decode' in options.draco)
    this._dracoService = options.draco
      ? 'decode' in options.draco
        ? options.draco
        : createDracoDecodeService(options.draco)
      : undefined
    this.loaderRegistry = createLoaderRegistry(options, this._dracoService)
    this.autoMount = options.autoMount ?? false
    this.opaqueScene.name = 'mesh:opaque'
    this.opaqueScene.background = null
    this.transparentScene.name = 'mesh:transparent'
    this.transparentScene.background = null
    addDefaultMeshLights(this.opaqueScene)
    addDefaultMeshLights(this.transparentScene)
  }

  /**
   * 把 backend 对应的 renderer 与上传上限交给 KTX2 服务。
   *
   * WebGL2 直接用 legacy renderer（上限来自 `capabilities.maxTextureSize`）；WebGPU 复用 engine 内部的
   * `WebGPURenderer` 供 r186 `KTX2Loader.detectSupport()` 探测压缩格式，上限从只读 graphics info 的
   * `deviceLimits.maxTextureDimension2D` 读取——不向公共 API 泄漏 renderer 或 device。
   */
  private bindKtx2Renderer(context: EngineContext): void {
    if (!this._ktx2Service) {
      return
    }

    const graphics = context.graphics as EngineContext['graphics'] | undefined
    if (graphics?.kind === 'webgpu') {
      const backend = graphics as typeof graphics & {
        getWebGPURenderer?: () => unknown
      }
      const renderer = backend.getWebGPURenderer?.()
      const limit = graphics.getInfo().webgpu?.deviceLimits.maxTextureDimension2D
      if (renderer && typeof limit === 'number' && Number.isFinite(limit)) {
        this._ktx2Service.setRenderer(renderer as never, limit)
        return
      }
      this.log?.warn('WebGPU 缺少 maxTextureDimension2D，KTX2 上传上限无法确定，将拒绝超尺寸纹理')
      return
    }

    this._ktx2Service.setRenderer(context.renderer)
  }

  /** 返回当前已注册格式，供诊断和自定义 Loader 验证。 */
  getRegisteredFormats(): string[] {
    return this.loaderRegistry.formats()
  }

  /** WebGPU 下预加载 NodeMaterial/TSL；WebGL2 不触达相关模块。 */
  async preload(context: EngineContext): Promise<void> {
    this.clipBackend = context.graphics?.kind ?? 'webgl2'
    if (this.clipBackend !== 'webgpu') return
    registerMeshClipWebGpuMaterialModule(await loadMeshClipWebGpuMaterialModule())
  }

  /** preload 失败时清理同步注册，避免后续材质误用半初始化状态。 */
  rollbackPreload(): void {
    registerMeshClipWebGpuMaterialModule(null)
    this.clipBackend = 'webgl2'
  }

  onInit(context: EngineContext): void {
    this.context = context
    this.clipBackend = context.graphics?.kind ?? 'webgl2'
    this.bindKtx2Renderer(context)
    this.meshRoot = context.sceneGraph.registerRoot(this.kind)
    this.log = context.logger.child('mesh')
    this.renderLog = context.logger.child('mesh:render')
    this.clipLog = context.logger.child('mesh:clip')
    this.log.debug(`插件初始化，格式: ${this.loaderRegistry.formats().join(', ')}`)
  }

  onStart(): void {
    this.context.getService('RenderPipeline').addRenderContributor(this)
    this.context.getService('PickingService').addProvider(this)
    this.started = true
    this.log?.debug('插件启动，已注册渲染与拾取贡献者')

    if (typeof this.context.hasService === 'function' && this.context.hasService('ClipService')) {
      const clipService = this.context.getService('ClipService')
      this.applyClipVolumes(clipService.volumes ?? [])
      this.clipSubscription = clipService.onChanged((volumes) => this.applyClipVolumes(volumes))
    }
  }

  onDestroy(): void {
    if (this.disposed) return
    this.disposed = true
    this.clipSubscription?.dispose()
    this.clipSubscription = null
    for (const controller of this.pendingLoads.values()) controller.abort('plugin destroyed')
    this.pendingLoads.clear()
    this._ktx2Service?.dispose()
    if (this._ownsDracoService) this._dracoService?.dispose()
    if (this.started) {
      this.context.getService('RenderPipeline').removeRenderContributor(this)
      this.context.getService('PickingService').removeProvider(this)
      this.started = false
    }
    this.dispose()
    this.clipBinding.dispose()
    this.clipStates.dispose()
    this.meshRoot?.removeFromParent()
    this.meshRoot = null
    this.log?.debug('插件销毁完成')
  }

  async load(request: MeshLoadRequest): Promise<MeshHandle> {
    this.ensureAvailable()
    validateMeshCoordinateOptions(request.coordinate)
    const id = request.id ?? nanoid()
    if (this.records.has(id) || this.pendingLoads.has(id)) {
      throw new Error(`Mesh 资产 id 已存在: "${id}"`)
    }

    const source = describeMeshSource(request)
    const loadStartedAt = getLoggerManager().now()
    const controller = new AbortController()
    this.pendingLoads.set(id, controller)
    const removeExternalAbort = this.linkAbortSignal(request.signal, controller)
    let lastProgress = 0
    let preview: MeshLoadingPreview | undefined
    let batcher: MeshPartBatcher<MeshLoadResult> | undefined
    let batchError: Error | undefined
    const closeBatch = () => batcher?.close()
    controller.signal.addEventListener('abort', closeBatch, { once: true })
    const reportProgress = (progress: number) => {
      const next = Math.max(lastProgress, Math.min(1, progress))
      lastProgress = next
      request.onProgress?.(next)
      this.context.events.emit('asset.progress', {
        id,
        kind: this.kind,
        progress: next,
        loaded: next,
        total: 1,
      })
    }

    this.context.events.emit('asset.loading', { id, kind: this.kind })

    try {
      const loader = this.resolveLoader(request, source)
      this.log?.debug(`开始加载，id: ${id}，格式: ${loader.format}，来源: ${source}`)
      const loaderRequest: MeshLoadRequest = {
        ...request,
        format: normalizeFormat(loader.format),
        signal: controller.signal,
        onProgress: undefined,
        onPartialLoad: undefined,
      }
      if (request.onPartialLoad) {
        batcher = new MeshPartBatcher(
          (parts) => {
            if (this.disposed || controller.signal.aborted) return
            if (!preview) {
              preview = new MeshLoadingPreview(normalizeFormat(loader.format), request.name ?? id)
              const handle = this.createHandle(id, request, preview)
              this.records.set(id, {
                handle,
                result: preview,
                pickIndex: new MeshPickIndex(preview.root),
                appearance: new MeshAppearanceController(handle, {
                  clipBinding: this.clipBinding,
                  clipBackend: this.clipBackend,
                  onUnsupportedClipMaterial: (materialType) =>
                    this.warnUnsupportedClipMaterial(materialType),
                }),
                mounted: false,
              })
              this.records.get(id)!.appearance.setClippingEnabled(this.clipStates.hasAnyVolumes)
            }
            const record = this.records.get(id)!
            const primitives = parts.flatMap((part) => {
              if (request.forceDoubleSided === true) forceDoubleSidedMaterials(part)
              return preview!.append(part, request.coordinate)
            })
            record.appearance.append(primitives)
            record.pickIndex.append(primitives)
            if (this.autoMount) this.mount(record.handle)
            request.onPartialLoad!(record.handle)
            this.context.renderInvalidation?.invalidate('mesh.partialLoad')
          },
          (error) => {
            batchError = error instanceof Error ? error : new Error(String(error))
            controller.abort(batchError)
          }
        )
      }
      const loaderContext: MeshLoadContext = {
        loadingManager: this.context.loadingManager,
        ktx2Service: this._ktx2Service,
        dracoService: this._dracoService,
        getLoadedMeshGpuBytes: () =>
          estimateLoadedMeshGpuBytes(
            [...this.records]
              .filter(([recordId]) => recordId !== id)
              .map(([, record]) => record.result)
          ),
        signal: controller.signal,
        onProgress: reportProgress,
        onPart: batcher
          ? (part) => {
              if (part.primitives.length > 0) batcher!.push(part)
            }
          : undefined,
      }
      const loaderPromise = loader.load(loaderRequest, loaderContext)
      const result = await this.awaitLoaderResult(
        batcher
          ? loaderPromise.then((loaded) => {
              try {
                batcher?.flush()
                return loaded
              } catch (error) {
                this.disposeResult(loaded)
                throw error
              }
            })
          : loaderPromise,
        controller.signal,
        source
      )

      if (this.disposed || controller.signal.aborted) {
        this.disposeResult(result)
        throw new MeshLoadCancelledError(source, { cause: controller.signal.reason })
      }

      if (request.forceDoubleSided === true) forceDoubleSidedMaterials(result)
      applyMeshCoordinateOptions(result, request.coordinate)
      const handle = preview ? this.records.get(id)!.handle : this.createHandle(id, request, result)
      if (preview) {
        this.records.get(id)!.appearance.replace(result.primitives)
        preview.complete(result)
        this.records.get(id)!.pickIndex.replace(result.primitives)
        this.context.renderInvalidation?.invalidate('mesh.loadComplete')
      } else
        this.records.set(id, {
          handle,
          result,
          pickIndex: new MeshPickIndex(result.root, result.primitives),
          appearance: new MeshAppearanceController(handle, {
            clipBinding: this.clipBinding,
            clipBackend: this.clipBackend,
            onUnsupportedClipMaterial: (materialType) =>
              this.warnUnsupportedClipMaterial(materialType),
          }),
          mounted: false,
        })
      this.records.get(id)!.appearance.setClippingEnabled(this.clipStates.hasAnyVolumes)
      if (lastProgress < 1) reportProgress(1)
      this.context.events.emit('asset.loaded', { id, kind: this.kind })
      const loadElapsedMs = getLoggerManager().now() - loadStartedAt
      this.log?.perf?.record?.('load', loadElapsedMs)
      this.log?.debug(
        `加载完成，id: ${id}，格式: ${result.format}，路径: ${
          result.report.adoptedPath ?? 'default'
        }，耗时: ${loadElapsedMs.toFixed(1)}ms，Primitive: ${
          result.report.stats.primitiveCount
        }，三角形: ${result.report.stats.triangleCount}`
      )
      if (this.autoMount) this.mount(handle)
      return handle
    } catch (cause) {
      const error = batchError ?? cause
      controller.abort(error)
      if (preview && this.records.get(id)?.result === preview) this.disposeAsset(id)
      if (!(error instanceof MeshLoadCancelledError)) {
        const normalized = error instanceof Error ? error : new Error(String(error))
        this.context.events.emit('asset.error', { id, kind: this.kind, error: normalized })
        this.log?.error(
          `加载失败，id: ${id}，耗时: ${(getLoggerManager().now() - loadStartedAt).toFixed(
            1
          )}ms，原因: ${normalized.message}`
        )
      } else {
        this.log?.debug(
          `加载取消，id: ${id}，耗时: ${(getLoggerManager().now() - loadStartedAt).toFixed(1)}ms`
        )
      }
      throw error
    } finally {
      batcher?.close()
      controller.signal.removeEventListener('abort', closeBatch)
      removeExternalAbort()
      if (this.pendingLoads.get(id) === controller) this.pendingLoads.delete(id)
    }
  }

  mount(handle: MeshHandle): void {
    const record = this.records.get(handle.id)
    if (!record || record.handle !== handle) {
      throw new Error(`不能挂载不属于当前 MeshPlugin 的资产: "${handle.id}"`)
    }
    if (record.mounted) return
    this.requireMeshRoot().add(handle.object3D)
    record.mounted = true
    this.context.sceneBounds.register({
      id: handle.id,
      kind: this.kind,
      object3D: handle.object3D,
      getLocalBoundingBox: () => handle.boundingBox,
      isVisible: () => handle.visible,
    })
    this.context.events.emit('asset.mounted', { id: handle.id, kind: this.kind })
    this.log?.debug(`资产挂载，id: ${handle.id}`)
  }

  unmount(id: AssetId): void {
    const record = this.records.get(id)
    if (!record?.mounted) return
    this.context.sceneBounds.unregister(this.kind, id)
    record.handle.object3D.removeFromParent()
    record.mounted = false
    this.context.events.emit('asset.unmounted', { id, kind: this.kind })
    this.log?.debug(`资产卸载，id: ${id}`)
  }

  getAsset(id: AssetId): MeshHandle | undefined {
    return this.records.get(id)?.handle
  }

  getAll(): MeshHandle[] {
    return Array.from(this.records.values(), (record) => record.handle)
  }

  setVisibility(id: AssetId, visible: boolean): void {
    const record = this.records.get(id)
    if (!record) return
    record.handle.visible = visible
    record.handle.object3D.visible = visible
    this.context.events.emit('asset.visibilityChanged', { id, visible })
  }

  /**
   * 设置 Mesh 资产的源外观或统一纯色外观。
   * @param id Mesh 资产标识。
   * @param appearance 要应用的运行时外观。
   */
  setAppearance(id: AssetId, appearance: MeshAppearance): void {
    const record = this.records.get(id)
    if (!record) return
    record.appearance.apply(appearance)
    this.context.renderInvalidation?.invalidate('mesh.appearance', { detail: id })
  }

  render(frame: ViewFrameContext, phase: RenderPhase, ctx: PhaseRenderContext): void {
    const renderClass = this.getRenderClassForPhase(phase)
    if (!renderClass || !this.hasVisibleMountedPrimitive(renderClass)) return

    this.clipBinding.bind(this.clipStates.get(frame.viewId).packet)

    const scene = phase === RenderPhase.MainTransparent ? this.transparentScene : this.opaqueScene
    const restoreVisibility = this.routePrimitiveVisibility(renderClass)
    const root = this.requireMeshRoot()
    const originalParent = root.parent
    const originalIndex = originalParent?.children.indexOf(root) ?? -1
    const backendKind = (ctx.graphics as { kind?: string } | undefined)?.kind ?? 'webgl2'
    const renderer = backendKind === 'webgl2' ? ctx.renderer : null
    const state = renderer ? saveRendererState(renderer) : null

    try {
      scene.add(root)
      if (renderer) {
        renderer.resetState()
        renderer.autoClear = false
        renderer.setRenderTarget(ctx.outputTarget)
        renderer.setViewport(
          ctx.viewport.x,
          ctx.viewport.y,
          ctx.viewport.width,
          ctx.viewport.height
        )
        renderer.setScissor(ctx.viewport.x, ctx.viewport.y, ctx.viewport.width, ctx.viewport.height)
        renderer.setScissorTest(true)
        renderer.render(scene, frame.camera)
      } else {
        // WebGPU 走受控 scene 提交：viewport/scissor/target 由 backend 依当前 View 决定。
        // WebGL2 保持既有路径——Mesh 依赖 legacy `resetState()` 清理外部 GL 状态缓存，
        // 这不是 GraphicsBackend 的通用契约，因此不做「类结构对称」重构。
        submitSceneInView(ctx, scene, frame.camera)
      }
      this.renderLog?.debug(
        `渲染完成，阶段: ${phase}，view: ${frame.viewId}，资产: ${this.records.size}`
      )
    } finally {
      restoreVisibility()
      root.removeFromParent()
      if (originalParent) restoreChildIndex(originalParent, root, originalIndex)
      if (state && renderer) restoreRendererState(renderer, state)
    }
  }

  pick(query: PickQuery): PickResult | null {
    if (query.kinds && !query.kinds.includes(this.kind)) return null
    const raycaster = new THREE.Raycaster()
    raycaster.ray.copy(query.ray)
    raycaster.near = query.minDistance ?? 0
    raycaster.layers.mask = query.camera.layers.mask
    const candidates: PickResult[] = []
    const clipState = this.clipStates.get(query.viewId).state
    const hasClip = clipState.volumes.length > 0

    for (const [assetId, record] of this.records) {
      if (!record.mounted || !record.handle.visible) continue
      if (query.assetIds && !query.assetIds.includes(assetId)) continue
      const objects = record.pickIndex.query(query.ray)
      const intersections = raycaster.intersectObjects(objects, false)
      const hit = intersections.find(
        (intersection) =>
          isPickPointAtOrBeyondMinDistance(query.ray, intersection.point, query.minDistance) &&
          (!hasClip ||
            !record.appearance.isClipSupported(intersection.object) ||
            !evaluateCompiledClipState(clipState, intersection.point).hidden)
      )
      if (!hit) continue

      const normal = hit.face?.normal
        .clone()
        .applyNormalMatrix(new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld))
      candidates.push({
        assetId,
        kind: this.kind,
        point: hit.point.clone(),
        distance: getPickRayDistance(query.ray, hit.point),
        normal,
        _raw: hit,
      })
    }

    candidates.sort((left, right) => left.distance - right.distance)
    return candidates[0] ?? null
  }

  dispose(id?: AssetId): void {
    if (id !== undefined) {
      this.disposeAsset(id)
      return
    }
    for (const controller of this.pendingLoads.values()) controller.abort('plugin disposed')
    this.pendingLoads.clear()
    for (const assetId of Array.from(this.records.keys())) this.disposeAsset(assetId)
  }

  private disposeAsset(id: AssetId): void {
    const pending = this.pendingLoads.get(id)
    pending?.abort('asset disposed')
    this.pendingLoads.delete(id)
    const record = this.records.get(id)
    if (!record) return

    this.unmount(id)
    this.records.delete(id)
    record.appearance.dispose()
    this.disposeResult(record.result)
    this.context.events.emit('asset.disposed', { id, kind: this.kind })
    this.log?.debug(`资产释放，id: ${id}`)
  }

  private createHandle(id: AssetId, request: MeshLoadRequest, result: MeshLoadResult): MeshHandle {
    return {
      id,
      kind: this.kind,
      name: request.name ?? request.url ?? id,
      visible: true,
      boundingBox: result.boundingBox,
      object3D: result.root,
      format: result.format,
      get report() {
        return result.report
      },
      get primitives() {
        return result.primitives
      },
      get batchSourceMap() {
        return result.batchSourceMap
      },
      getPrimitivesBySourceNode: (sourceNodeId) =>
        result.sourcePrimitiveMap.get(sourceNodeId) ?? [],
    }
  }

  private resolveLoader(
    request: MeshLoadRequest,
    source: string
  ): AssetLoader<MeshLoadRequest, MeshLoadResult> {
    const loader = request.format
      ? this.loaderRegistry.get(normalizeFormat(request.format))
      : this.loaderRegistry.detect(request)
    if (!loader) {
      throw new MeshFormatNotSupportedError(source, this.loaderRegistry.formats())
    }
    return loader
  }

  private awaitLoaderResult(
    loaderPromise: Promise<MeshLoadResult>,
    signal: AbortSignal,
    source: string
  ): Promise<MeshLoadResult> {
    return new Promise((resolve, reject) => {
      let settled = false
      const handleAbort = () => {
        if (settled) return
        settled = true
        signal.removeEventListener('abort', handleAbort)
        reject(new MeshLoadCancelledError(source, { cause: signal.reason }))
      }
      signal.addEventListener('abort', handleAbort, { once: true })
      if (signal.aborted) handleAbort()

      loaderPromise.then(
        (result) => {
          if (settled) {
            this.disposeResult(result)
            return
          }
          settled = true
          signal.removeEventListener('abort', handleAbort)
          resolve(result)
        },
        (error) => {
          if (settled) return
          settled = true
          signal.removeEventListener('abort', handleAbort)
          reject(error)
        }
      )
    })
  }

  private linkAbortSignal(
    signal: AbortSignal | undefined,
    controller: AbortController
  ): () => void {
    if (!signal) return () => {}
    const abort = () => controller.abort(signal.reason)
    if (signal.aborted) abort()
    else signal.addEventListener('abort', abort, { once: true })
    return () => signal.removeEventListener('abort', abort)
  }

  private getRenderClassForPhase(phase: RenderPhase): MeshRenderClass | null {
    if (phase === RenderPhase.MainOpaque) return 'opaque'
    if (phase === RenderPhase.MainTransparent) return 'blend'
    return null
  }

  private matchesRenderClass(
    primitiveClass: MeshRenderClass,
    phaseClass: MeshRenderClass
  ): boolean {
    return phaseClass === 'opaque'
      ? primitiveClass === 'opaque' || primitiveClass === 'mask'
      : primitiveClass === 'blend'
  }

  private hasVisibleMountedPrimitive(renderClass: MeshRenderClass): boolean {
    for (const record of this.records.values()) {
      if (!record.mounted || !record.handle.visible) continue
      if (
        record.handle.primitives.some(
          (primitive) =>
            primitive.object.visible &&
            this.matchesRenderClass(this.getPrimitiveRenderClass(primitive), renderClass)
        )
      ) {
        return true
      }
    }
    return false
  }

  private routePrimitiveVisibility(renderClass: MeshRenderClass): () => void {
    const saved = new Map<THREE.Object3D, boolean>()
    for (const record of this.records.values()) {
      for (const primitive of record.handle.primitives) {
        saved.set(primitive.object, primitive.object.visible)
        primitive.object.visible =
          record.mounted &&
          record.handle.visible &&
          primitive.object.visible &&
          this.matchesRenderClass(this.getPrimitiveRenderClass(primitive), renderClass)
      }
    }
    return () => {
      for (const [object, visible] of saved) object.visible = visible
    }
  }

  private disposeResult(result: MeshLoadResult): void {
    try {
      result.dispose()
    } catch (error) {
      this.log?.error(
        `资源释放失败，格式: ${result.format}，原因: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
    }
  }

  private getPrimitiveRenderClass(
    primitive: MeshLoadResult['primitives'][number]
  ): MeshRenderClass {
    const material = Array.isArray(primitive.object.material)
      ? primitive.object.material[0]
      : primitive.object.material
    return material ? classifyMeshMaterial(material) : primitive.renderClass
  }

  private requireMeshRoot(): THREE.Group {
    if (!this.meshRoot) throw new Error('MeshPlugin 尚未初始化')
    return this.meshRoot
  }

  private ensureAvailable(): void {
    if (this.disposed) throw new MeshLoadCancelledError('(插件已销毁)')
    this.requireMeshRoot()
  }

  private applyClipVolumes(volumes: ReadonlyArray<ClipVolume>): void {
    if (this.disposed) return
    const droppedCount = this.clipStates.update(volumes)
    const enabled = this.clipStates.hasAnyVolumes
    for (const record of this.records.values()) {
      record.appearance.setClippingEnabled(enabled)
    }
    if (droppedCount > 0) {
      this.clipLog?.warn(`忽略无效或超容量的 Mesh 裁切体，数量: ${droppedCount}`)
    }
    this.context.renderInvalidation?.invalidate('mesh.clipVolumes')
  }

  private warnUnsupportedClipMaterial(materialType: string): void {
    if (this.warnedClipMaterialTypes.has(materialType)) return
    this.warnedClipMaterialTypes.add(materialType)
    this.clipLog?.warn(`材质不支持 Mesh 裁切，已保持原渲染与拾取，类型: ${materialType}`)
  }
}
